import React from 'react';
import { cn } from '../../poker-ui/utils/cn';
import { belotComboLabelShort, comboLabelPrefixFromComboKey } from './belotComboLabelUi';
import { BELOT_FELT_LABEL_NEUTRAL, belotMatchDropTonePairFromLabel } from './BelotTableSharedUi';
import { BelotSeatFloatingBadge } from './belotSeatFloatingBadge';

const { detectCombinations, declarationComboKey } = require('../../shared/game/belot/armenian/declarations');

/**
 * @param {string} comboKey
 */
function shortFromComboKey(comboKey) {
  return belotComboLabelShort(comboLabelPrefixFromComboKey(comboKey));
}

/**
 * Короткие подписи комбо как у {@link BelotSeatSignalStrip}.
 * @param {string[]|null|undefined} stripKeys
 * @param {string} phase
 * @param {string} declarationMode
 * @param {number[]|null|undefined} casualDeclareHand
 * @param {{ battleOutcome?: 'team_a'|'team_b'|'tie'|null, battleApply?: boolean, seatTeamIndex?: number }} [battleOpts]
 * @returns {string[]}
 */
export function collectDeclarePillLabels(stripKeys, phase, declarationMode, casualDeclareHand, battleOpts) {
  const bo =
    battleOpts && typeof battleOpts === 'object'
      ? battleOpts
      : /** @type {{ battleOutcome?: unknown, battleApply?: unknown, seatTeamIndex?: unknown }} */ ({});
  const battleOutcome = bo.battleOutcome;
  const battleApply = bo.battleApply === true;
  const seatTeamIx = bo.seatTeamIndex;
  if (
    battleApply &&
    (battleOutcome === 'team_a' || battleOutcome === 'team_b') &&
    typeof seatTeamIx === 'number' &&
    Number.isFinite(seatTeamIx)
  ) {
    const winningTeam = battleOutcome === 'team_a' ? 0 : 1;
    if (seatTeamIx !== winningTeam) return [];
  }
  const declMode = declarationMode === 'hardcore' ? 'hardcore' : 'casual';
  const keys = Array.isArray(stripKeys) ? stripKeys.filter(Boolean) : [];
  if (keys.length > 0) {
    const seen = new Set();
    /** @type {string[]} */
    const labels = [];
    for (const k of keys) {
      const sh = shortFromComboKey(String(k));
      if (!sh || seen.has(sh)) continue;
      seen.add(sh);
      labels.push(sh);
    }
    return labels.slice(0, 5);
  }
  if (phase === 'declare' && declMode === 'casual' && Array.isArray(casualDeclareHand) && casualDeclareHand.length === 8) {
    try {
      const combos = detectCombinations(casualDeclareHand);
      combos.sort((a, b) => (b.points || 0) - (a.points || 0));
      const seen = new Set();
      /** @type {string[]} */
      const labels = [];
      for (const c of combos) {
        const k = declarationComboKey(c);
        if (seen.has(k)) continue;
        seen.add(k);
        const sh = belotComboLabelShort(c.label);
        if (sh) labels.push(sh);
      }
      return labels.slice(0, 5);
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Капля над местом в матче — тот же силуэт, что у торговой капли; цвет по масти / терцу из текста.
 * @param {{ label: string, isCurrentTurn?: boolean, variant?: 'combo'|'bela' }} props — `variant` оставлен для совместимости, тон берётся из `label`.
 */
export function SeatBelotMatchDropBadge({ label, isCurrentTurn = false, variant: _variant = 'combo' }) {
  const text = String(label || '').trim();
  if (!text) return null;
  const pair = belotMatchDropTonePairFromLabel(text);
  return (
    <BelotSeatFloatingBadge
      bubbleToneClass={pair.bubble}
      tailToneClass={pair.tail}
      isCurrentTurn={isCurrentTurn}
      title={text}
    >
      <span
        className={cn(
          'line-clamp-2 break-words text-[10px] font-extrabold leading-tight',
          BELOT_FELT_LABEL_NEUTRAL,
        )}
      >
        {text}
      </span>
    </BelotSeatFloatingBadge>
  );
}
